/**
 * The words the product uses for one appointment, in one place.
 *
 * The clinic app and the appointment report were drifting: `pending` rendered
 * as "Scheduled" on the calendar and "Unconfirmed" in the report, and one
 * `no_show` was variously "No Show", "No-show", "Missed" and "did not turn up"
 * across six surfaces. An owner who filters the board for one word and reads
 * another in the report cannot tell they are the same pile, so both sides now
 * read their labels from here.
 *
 * Exported as flat constants rather than a record keyed by status, because the
 * two consumers key the same five states differently - the clinic app by the
 * stored `no_show`, the report by the `noShow` of its counts payload - and a
 * shared vocabulary should not force either to translate.
 *
 * Two voices, deliberately, because they answer different questions:
 *
 * - STATUS is what the row *is*, for chips, columns, legends and table headers.
 *   Nouns and adjectives: "No show", "Attended".
 * - ANSWER is what a person *did*, for the buttons and radios that record an
 *   outcome. Sentences about them: "No, they didn't come".
 *
 * Keeping them apart is why the attendance radio can stay conversational while
 * the pie chart legend stays scannable, without either inventing its own word
 * for the same state.
 */
export const APPOINTMENT_STATUS_LABEL = {
  PENDING: "Unconfirmed",
  CONFIRMED: "Confirmed",
  // "Attended", not "Completed": it pairs with "No show" as the two halves of
  // the question the outcome flow actually asks, and a visit that happened is
  // what the owner is counting - not a row that reached a terminal state.
  COMPLETED: "Attended",
  CANCELLED: "Cancelled",
  NO_SHOW: "No show",
  /** Not a stored status - a thing that happened to one. */
  MOVED: "Moved",
} as const;

/**
 * The answer voice. These are the words on the controls that record what
 * happened, and they are shared by the attendance radio and the banner's split
 * button so tapping either means the same thing.
 */
export const ATTENDANCE_ANSWER_LABEL = {
  SHOWED_UP: "Yes, they came in",
  NO_SHOW: "No, they didn't come",
  /** Side by side on the banner's two buttons, where the sentences won't fit. */
  SHOWED_UP_SHORT: "They came in",
  NO_SHOW_SHORT: "They didn't come",
  /** Under a heading that already asks "Did they turn up?", where a sentence would ask it twice. */
  SHOWED_UP_TERSE: "Yes",
  NO_SHOW_TERSE: "No",
} as const;

/**
 * The second question, asked only after "they didn't come".
 *
 * Phrased about the patient rather than about the record - the agent is
 * repeating what they were just told, not picking a database state. "Moved it"
 * still reads as one action to them even though it now settles the missed slot
 * and books a fresh one underneath.
 */
export const NO_SHOW_NEXT_LABEL = {
  WONT_COME: "They're not coming back",
  RESCHEDULED: "They moved it to another day",
  WILL_TELL_LATER: "They'll tell us when",
} as const;

/** Said under the one answer that moves the conversation; the others are silent. */
export const NO_SHOW_NEXT_CONSEQUENCE = {
  WILL_TELL_LATER: "Snoozes the conversation until the date and reminds you then.",
} as const;

/**
 * Everyone who books is a "patient" only once they are one.
 *
 * Somebody who booked a free consultation is a lead the clinic is still selling
 * to - calling them a patient in the one flow whose job is to record whether
 * they bought flattens exactly the distinction the sales agent is working.
 *
 * Only call this where the booking type is actually known. A surface that mixes
 * both populations - the repeat-no-show list, the patients card - has no way to
 * tell them apart from what the API sends, so it says "people" rather than
 * picking one and being wrong about half the rows.
 */
export const personNoun = (isFreeConsultation: boolean) =>
  isFreeConsultation ? "lead" : "patient";

export const personNounPlural = (isFreeConsultation: boolean) =>
  isFreeConsultation ? "leads" : "patients";
